import React, { useState, useEffect } from 'react';
import { collection, addDoc, deleteDoc, doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { PencilIcon, Save, Plus, X, Trash2 } from 'lucide-react';
import { db } from '../utils/firebase';
import { Link } from 'react-router-dom';
import { APP_VERSION } from '../utils/version';

const NoteManager = () => {
  const [categories, setCategories] = useState([]);
  const [notes, setNotes] = useState([]);
  const [editingNote, setEditingNote] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newNote, setNewNote] = useState({ text: '', category: '' });
  const [newCategory, setNewCategory] = useState({ title: '' });
  
  const categoriesRef = collection(db, 'noteCategories');
  const notesRef = collection(db, 'prewrittenNotes');

  // Load data
  useEffect(() => {
    const unsubCategories = onSnapshot(categoriesRef, (snapshot) => {
      const data = [];
      snapshot.forEach((doc) => data.push({ id: doc.id, ...doc.data() }));
      setCategories(data);
    });

    const unsubNotes = onSnapshot(notesRef, (snapshot) => {
      const data = [];
      snapshot.forEach((doc) => data.push({ id: doc.id, ...doc.data() }));
      setNotes(data);
    });

    return () => {
      unsubCategories();
      unsubNotes();
    };
  }, []);

  // Category operations
  const addCategory = async () => {
    if (!newCategory.title.trim()) return;
    await addDoc(categoriesRef, newCategory);
    setNewCategory({ title: '' });
  };

  const updateCategory = async (id) => {
    await updateDoc(doc(db, 'noteCategories', id), editingCategory);
    setEditingCategory(null);
  };

  const deleteCategory = async (id) => {
    const hasNotes = notes.some(note => note.category === id);
    if (hasNotes) {
      if (!confirm('This will delete all notes in this category. Continue?')) return;
      notes.forEach(async (note) => {
        if (note.category === id) {
          await deleteDoc(doc(db, 'prewrittenNotes', note.id));
        }
      });
    }
    await deleteDoc(doc(db, 'noteCategories', id));
  };

  // Note operations
  const addNote = async () => {
    if (!newNote.text.trim() || !newNote.category) return;
    await addDoc(notesRef, newNote);
    setNewNote({ text: '', category: '' });
  };

  const updateNote = async (id) => {
    await updateDoc(doc(db, 'prewrittenNotes', id), editingNote);
    setEditingNote(null);
  };

  const deleteNote = async (id) => {
    if (!confirm('Delete this note?')) return;
    await deleteDoc(doc(db, 'prewrittenNotes', id));
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Manage Prewritten Notes</CardTitle>
            <p className="text-xs text-slate-500 mt-1">v{APP_VERSION}</p>
          </div>
          <Link 
            to="/"
            className="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
          >
            Back to Form
          </Link>
        </CardHeader>
        <CardContent>
          {/* Add New Category */}
          <div className="mb-8 p-4 bg-slate-50 rounded-lg">
            <h3 className="font-medium mb-4">Add New Category</h3>
            <div className="flex gap-4">
              <input
                type="text"
                placeholder="Category name..."
                className="flex-1 px-3 py-2 border rounded-md"
                value={newCategory.title}
                onChange={(e) => setNewCategory({ title: e.target.value })}
              />
              <button
                onClick={addCategory}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
              >
                Add Category
              </button>
            </div>
          </div>

          {/* Add New Note */}
          <div className="mb-8 p-4 bg-slate-50 rounded-lg">
            <h3 className="font-medium mb-4">Add New Note</h3>
            <div className="space-y-4">
              <select
                className="w-full px-3 py-2 border rounded-md"
                value={newNote.category}
                onChange={(e) => setNewNote({ ...newNote, category: e.target.value })}
              >
                <option value="">Select Category...</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.title}</option>
                ))}
              </select>
              <textarea
                placeholder="Note text..."
                className="w-full px-3 py-2 border rounded-md"
                rows={3}
                value={newNote.text}
                onChange={(e) => setNewNote({ ...newNote, text: e.target.value })}
              />
              <button
                onClick={addNote}
                className="w-full px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
              >
                Add Note
              </button>
            </div>
          </div>

          {/* Categories and Notes List */}
          <div className="space-y-6">
            {categories.map(category => (
              <div key={category.id} className="border rounded-lg">
                {/* Category Header */}
                <div className="bg-slate-100 p-4">
                  {editingCategory?.id === category.id ? (
                    <div className="flex gap-4">
                      <input
                        type="text"
                        className="flex-1 px-3 py-1 border rounded-md"
                        value={editingCategory.title}
                        onChange={(e) => setEditingCategory({
                          ...editingCategory,
                          title: e.target.value
                        })}
                      />
                      <button
                        onClick={() => updateCategory(category.id)}
                        className="text-green-600 hover:text-green-700"
                      >
                        <Save className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => setEditingCategory(null)}
                        className="text-slate-600 hover:text-slate-700"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex justify-between items-center">
                      <h3 className="font-medium">{category.title}</h3>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditingCategory(category)}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => deleteCategory(category.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Category Notes */}
                <div className="p-4 space-y-4">
                  {notes
                    .filter(note => note.category === category.id)
                    .map(note => (
                      <div key={note.id} className="border rounded-lg p-4">
                        {editingNote?.id === note.id ? (
                          <div className="space-y-4">
                            <textarea
                              className="w-full px-3 py-2 border rounded-md"
                              rows={3}
                              value={editingNote.text}
                              onChange={(e) => setEditingNote({
                                ...editingNote,
                                text: e.target.value
                              })}
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => updateNote(note.id)}
                                className="px-3 py-1 bg-green-500 text-white rounded-md hover:bg-green-600"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => setEditingNote(null)}
                                className="px-3 py-1 bg-slate-500 text-white rounded-md hover:bg-slate-600"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex justify-between gap-4">
                            <div className="flex-1">
                              <p className="mb-2">{note.text}</p>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => setEditingNote(note)}
                                className="text-blue-600 hover:text-blue-700"
                              >
                                <PencilIcon className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => deleteNote(note.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NoteManager; 